import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { getAppSocket, disconnectAppSocket } from '../lib/wesSocket';
import {
  PlusIcon, ChevronLeftIcon, ChevronRightIcon, TrashIcon,
  LinkIcon, ArrowTopRightOnSquareIcon, CalendarDaysIcon, ArrowPathIcon,
  Cog6ToothIcon, MagnifyingGlassIcon, XMarkIcon,
} from '@heroicons/react/24/outline';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tip } from '../components/ui/tip';
import { DatePicker } from '../components/ui/date-picker';
import { confirmDialog } from '../components/ConfirmDialog';

const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const DOW = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

interface GEvent { id: string; title: string; description?: string; location?: string; start: string; end?: string; allDay?: boolean; htmlLink?: string; color?: string | null; calendarId?: string | null; calendarName?: string; readOnly?: boolean; hidden?: boolean; }
interface Status { configured: boolean; connected: boolean; email?: string | null; calendarId?: string; connectedAt?: string | null; redirectUri?: string; hasClientId?: boolean; realtime?: boolean; realtimeChannels?: number; }

function buildGrid(month: number, year: number) {
  const first = new Date(year, month - 1, 1);
  const dim = new Date(year, month, 0).getDate();
  const off = (first.getDay() + 6) % 7;
  const cells: Array<{ day: number; inMonth: boolean }> = [];
  const prev = new Date(year, month - 1, 0).getDate();
  for (let i = off - 1; i >= 0; i--) cells.push({ day: prev - i, inMonth: false });
  for (let d = 1; d <= dim; d++) cells.push({ day: d, inMonth: true });
  while (cells.length % 7 !== 0) { const o = cells.filter(c => !c.inMonth && c.day < 10); cells.push({ day: o.length + 1, inMonth: false }); }
  const weeks: typeof cells[] = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

const fmtTime = (iso: string, allDay?: boolean) => {
  if (allDay) return '';
  const d = new Date(iso);
  return d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
};

// Fecha local de un evento. Para eventos de todo el día Google manda "2026-06-20"
// (sin hora): si se parsea con new Date() se interpreta como UTC y se corre un día
// hacia atrás en husos negativos (Argentina GMT-3). Acá lo armamos como fecha local.
const eventLocalDate = (e: GEvent): Date => {
  const s = String(e.start || '');
  if (e.allDay || /^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, mo, da] = s.slice(0, 10).split('-').map(Number);
    return new Date(y, (mo || 1) - 1, da || 1);
  }
  return new Date(s);
};

const GoogleCalendarView: React.FC = () => {
  const { user, hasPermission } = useAuth();
  const isAdmin = user?.role === 'admin' || hasPermission('manage_settings');
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState<Status | null>(null);
  const [events, setEvents] = useState<GEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingEvents, setLoadingEvents] = useState(false);

  const today = new Date();
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [year, setYear] = useState(today.getFullYear());

  const [editEvent, setEditEvent] = useState<GEvent | null>(null);
  const [newOnDay, setNewOnDay] = useState<number | null>(null);
  const [reconfig, setReconfig] = useState(false);
  const [showHidden, setShowHidden] = useState(false);
  const [search, setSearch] = useState('');
  const [manageOpen, setManageOpen] = useState(false);

  const fetchStatus = useCallback(async () => {
    try { const r = await axios.get('/api/google-calendar/status'); setStatus(r.data); }
    catch { setStatus({ configured: false, connected: false }); }
    finally { setLoading(false); }
  }, []);

  const fetchEvents = useCallback(async () => {
    if (!status?.connected) return;
    setLoadingEvents(true);
    try {
      const timeMin = new Date(year, month - 1, 1).toISOString();
      const timeMax = new Date(year, month, 0, 23, 59, 59).toISOString();
      const r = await axios.get('/api/google-calendar/events', { params: { timeMin, timeMax } });
      setEvents(r.data.events || []);
    } catch (e: any) { toast.error(e.response?.data?.error || 'Error al cargar eventos'); }
    finally { setLoadingEvents(false); }
  }, [status?.connected, month, year]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);
  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  // Refrescar al volver a la pestaña (ej: editaste en Google y volviste al sistema)
  useEffect(() => {
    const onFocus = () => { if (document.visibilityState === 'visible') fetchEvents(); };
    document.addEventListener('visibilitychange', onFocus);
    window.addEventListener('focus', onFocus);
    return () => { document.removeEventListener('visibilitychange', onFocus); window.removeEventListener('focus', onFocus); };
  }, [fetchEvents]);

  // Tiempo real: el server avisa por socket cuando Google notifica un cambio
  useEffect(() => {
    if (!status?.connected) return;
    const socket = getAppSocket();
    const onChange = () => fetchEvents();
    const subscribe = () => socket.emit('gcal:subscribe');
    socket.on('gcal:changed', onChange);
    socket.on('connect', subscribe);
    if (socket.connected) subscribe();
    return () => {
      socket.emit('gcal:unsubscribe');
      socket.off('gcal:changed', onChange);
      socket.off('connect', subscribe);
      disconnectAppSocket();
    };
  }, [status?.connected, fetchEvents]);

  // Manejar retorno del OAuth
  useEffect(() => {
    const g = params.get('gcal');
    if (g === 'connected') { toast.success('Google Calendar conectado'); fetchStatus(); params.delete('gcal'); setParams(params, { replace: true }); }
    else if (g === 'error') { toast.error('Error: ' + (params.get('msg') || 'no se pudo conectar')); params.delete('gcal'); params.delete('msg'); setParams(params, { replace: true }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async () => {
    try { const r = await axios.get('/api/google-calendar/auth-url'); window.location.href = r.data.url; }
    catch (e: any) { toast.error(e.response?.data?.error || 'Error al generar la conexión'); }
  };
  const disconnect = async () => {
    if (!(await confirmDialog({ title: 'Desconectar', description: '¿Desconectar la cuenta de Google? Los eventos dejarán de mostrarse.', confirmText: 'Desconectar' }))) return;
    try { await axios.post('/api/google-calendar/disconnect'); toast.success('Desconectado'); setEvents([]); fetchStatus(); }
    catch { toast.error('Error'); }
  };
  const [activatingRt, setActivatingRt] = useState(false);
  const activateRealtime = async () => {
    setActivatingRt(true);
    try {
      const r = await axios.post('/api/google-calendar/watch');
      toast.success(`Tiempo real activado (${r.data.channels} calendario${r.data.channels > 1 ? 's' : ''})`);
      fetchStatus();
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'No se pudo activar. ¿Está verificado el dominio en Google?');
    } finally { setActivatingRt(false); }
  };

  const hiddenCount = useMemo(() => events.filter(e => e.hidden).length, [events]);

  const eventsByDay = useMemo(() => {
    const q = search.trim().toLowerCase();
    const m: Record<number, GEvent[]> = {};
    events.forEach(e => {
      if (e.hidden && !showHidden) return;
      if (q && !(`${e.title} ${e.description || ''} ${e.location || ''}`.toLowerCase().includes(q))) return;
      const d = eventLocalDate(e);
      if (d.getMonth() + 1 === month && d.getFullYear() === year) (m[d.getDate()] = m[d.getDate()] || []).push(e);
    });
    return m;
  }, [events, month, year, showHidden, search]);

  const toggleHide = useCallback(async (e: GEvent) => {
    try {
      if (e.hidden) { await axios.delete(`/api/google-calendar/events/${e.id}/hide`); toast.success('Evento visible de nuevo'); }
      else { await axios.post(`/api/google-calendar/events/${e.id}/hide`, { calendarId: e.calendarId, title: e.title }); toast.success('Evento oculto'); }
      setEditEvent(null);
      fetchEvents();
    } catch { toast.error('No se pudo actualizar'); }
  }, [fetchEvents]);

  const weeks = useMemo(() => buildGrid(month, year), [month, year]);
  const isToday = (d: number) => today.getDate() === d && today.getMonth() + 1 === month && today.getFullYear() === year;

  const prevMonth = () => { if (month === 1) { setMonth(12); setYear(y => y - 1); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 12) { setMonth(1); setYear(y => y + 1); } else setMonth(m => m + 1); };

  return (
    <div className="space-y-6 p-3 sm:p-6">
      {/* Header */}
      <div className="flex flex-col gap-4 lg:flex-row lg:justify-between lg:items-start">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Eventos · Google Calendar</h2>
          <p className="text-sm text-muted-foreground mt-0.5">Feriados, carreras, campañas y fechas del calendario del taller</p>
        </div>
        {status?.connected && (
          <div className="flex flex-wrap gap-2 items-center w-full lg:w-auto">
            <Badge variant="secondary" className="gap-1.5 max-w-full truncate"><span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" /> {status.email || 'Conectado'}</Badge>
            <Button size="sm" className="h-9 rounded-lg gap-1.5 bg-black dark:bg-white text-white dark:text-black hover:bg-black/90 dark:hover:bg-white/90" onClick={() => setNewOnDay(today.getMonth() + 1 === month && today.getFullYear() === year ? today.getDate() : 1)}>
              <PlusIcon className="h-4 w-4" /> Nuevo evento
            </Button>
            <Tip label="Gestionar calendarios"><Button variant="outline" size="sm" className="h-9 rounded-lg gap-1.5" onClick={() => setManageOpen(true)}>
              <Cog6ToothIcon className="h-4 w-4" /> Calendarios
            </Button></Tip>
            <Button variant="outline" size="sm" className="h-9 rounded-lg gap-1.5 text-red-600 border-red-500/30 hover:bg-red-500/10" onClick={disconnect}>Desconectar</Button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" /></div>
      ) : (!status?.configured || reconfig) ? (
        isAdmin ? (
          <CredentialsForm status={status} canCancel={reconfig} onCancel={() => setReconfig(false)} onSaved={() => { setReconfig(false); fetchStatus(); }} />
        ) : (
          <Card><CardContent className="py-16 text-center space-y-3">
            <CalendarDaysIcon className="h-12 w-12 text-muted-foreground mx-auto" />
            <p className="font-medium">Google Calendar no está configurado</p>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">Pedile al administrador que cargue las credenciales de Google para activar esta sección.</p>
          </CardContent></Card>
        )
      ) : !status?.connected ? (
        <Card><CardContent className="py-16 text-center space-y-4">
          <CalendarDaysIcon className="h-12 w-12 text-muted-foreground mx-auto" />
          <div>
            <p className="font-medium">Conectá el Google Calendar del taller</p>
            <p className="text-sm text-muted-foreground">Una vez conectado vas a ver y editar los eventos desde acá.</p>
          </div>
          <Button onClick={connect} className="bg-black dark:bg-white text-white dark:text-black gap-2">
            <LinkIcon className="h-4 w-4" /> Conectar Google Calendar
          </Button>
          {isAdmin && (
            <div>
              <button onClick={() => setReconfig(true)} className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2">
                Reconfigurar credenciales de Google
              </button>
            </div>
          )}
        </CardContent></Card>
      ) : (
        <>
          {/* Navegación de mes */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="icon" className="h-9 w-9 rounded-lg" onClick={prevMonth}><ChevronLeftIcon className="h-4 w-4" /></Button>
              <span className="text-lg font-semibold min-w-[140px] sm:min-w-[160px] text-center">{MONTHS[month - 1]} {year}</span>
              <Button variant="outline" size="icon" className="h-9 w-9 rounded-lg" onClick={nextMonth}><ChevronRightIcon className="h-4 w-4" /></Button>
              <Button variant="ghost" size="sm" className="h-9 rounded-lg" onClick={() => { setMonth(today.getMonth() + 1); setYear(today.getFullYear()); }}>Hoy</Button>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {loadingEvents && <span className="text-xs text-muted-foreground">Cargando...</span>}
              {hiddenCount > 0 && (
                <Tip label="Eventos que ocultaste desde ROCAM"><Button variant="ghost" size="sm" className="h-9 rounded-lg text-xs text-muted-foreground" onClick={() => setShowHidden(s => !s)}>
                  {showHidden ? 'Ocultar ' : 'Mostrar '}{hiddenCount} oculto{hiddenCount > 1 ? 's' : ''}
                </Button></Tip>
              )}
              {status?.realtime ? (
                <Tip label="Se actualiza solo cuando cambia en Google"><Badge variant="secondary" className="gap-1.5 h-9 px-3">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                  </span>
                  En vivo
                </Badge></Tip>
              ) : isAdmin ? (
                <Tip label="Activar actualización en tiempo real (requiere dominio verificado en Google)"><Button variant="outline" size="sm" className="h-9 rounded-lg gap-1.5" onClick={activateRealtime} disabled={activatingRt}>
                  <ArrowPathIcon className={`h-4 w-4 ${activatingRt ? 'animate-spin' : ''}`} /> {activatingRt ? 'Activando...' : 'Activar tiempo real'}
                </Button></Tip>
              ) : null}
            </div>
          </div>

          {/* Buscador de eventos */}
          <div className="relative w-full sm:max-w-sm">
            <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar eventos del mes..." className="pl-9 pr-9 h-9" />
            {search && (
              <Tip label="Limpiar"><button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                <XMarkIcon className="h-4 w-4" />
              </button></Tip>
            )}
          </div>

          {/* Grilla */}
          <Card><CardContent className="p-3 overflow-x-auto">
            <div className="min-w-[760px]">
              <div className="grid grid-cols-7 gap-2 mb-2">
                {DOW.map((d, i) => <div key={d} className={`text-xs font-semibold uppercase tracking-wide px-2 ${i >= 5 ? 'text-muted-foreground/60' : 'text-muted-foreground'}`}>{d}</div>)}
              </div>
              <div className="space-y-2">
                {weeks.map((week, wi) => (
                  <div key={wi} className="grid grid-cols-7 gap-2">
                    {week.map((cell, ci) => {
                      if (!cell.inMonth) return <div key={ci} className="rounded-lg border border-dashed border-border/40 min-h-[110px] p-2 opacity-40"><span className="text-xs text-muted-foreground">{cell.day}</span></div>;
                      const evs = eventsByDay[cell.day] || [];
                      const tdy = isToday(cell.day);
                      return (
                        <div key={ci}
                          className={`rounded-lg border min-h-[110px] p-2 flex flex-col group ${tdy ? 'ring-2 ring-blue-500/50 border-blue-500/30' : 'border-border'} hover:bg-muted/20 transition-colors`}>
                          <div className="flex items-center gap-1.5 mb-1">
                            <span className={`text-base font-bold ${tdy ? 'text-blue-600 dark:text-blue-400' : ''}`}>{cell.day}</span>
                            {tdy && <span className="text-[10px] font-bold bg-blue-500 text-white px-1.5 py-0.5 rounded">Hoy</span>}
                            <Tip label="Agregar evento"><button onClick={() => setNewOnDay(cell.day)} className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground">
                              <PlusIcon className="h-3.5 w-3.5" />
                            </button></Tip>
                          </div>
                          <div className="space-y-1">
                            {evs.map(e => (
                              <Tip key={`${e.calendarId}-${e.id}`} label={e.calendarName ? `${e.title} · ${e.calendarName}` : e.title}><button onClick={() => setEditEvent(e)}
                                className={`w-full text-left text-[11px] px-1.5 py-1 rounded bg-muted/60 hover:bg-muted transition-colors truncate flex items-center gap-1 ${e.hidden ? 'opacity-40 line-through' : ''}`}>
                                <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: e.color || '#3b82f6' }} />
                                {!e.allDay && <span className="font-mono text-[9px] opacity-70 shrink-0">{fmtTime(e.start)}</span>}
                                <span className="truncate">{e.title}</span>
                              </button></Tip>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </CardContent></Card>
        </>
      )}

      {/* Modal crear/editar evento */}
      {(editEvent || newOnDay != null) && (
        <EventModal
          event={editEvent}
          defaultDay={newOnDay}
          month={month} year={year}
          onToggleHide={toggleHide}
          onClose={() => { setEditEvent(null); setNewOnDay(null); }}
          onSaved={() => { setEditEvent(null); setNewOnDay(null); fetchEvents(); }}
        />
      )}

      {/* Panel de gestión de calendarios */}
      {manageOpen && (
        <CalendarsManager onClose={() => setManageOpen(false)} onChanged={() => { fetchStatus(); fetchEvents(); }} />
      )}
    </div>
  );
};

// ── Formulario de credenciales (solo admin) ───────────────────────────────
const CredentialsForm: React.FC<{ status: Status | null; onSaved: () => void; canCancel?: boolean; onCancel?: () => void }> = ({ status, onSaved, canCancel, onCancel }) => {
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [saving, setSaving] = useState(false);
  const redirectUri = status?.redirectUri || 'https://app.rocam.com.ar/api/google-calendar/callback';

  const save = async () => {
    if (!clientId.includes('.apps.googleusercontent.com')) { toast.error('El Client ID debe terminar en .apps.googleusercontent.com'); return; }
    if (!clientSecret.trim()) { toast.error('Pegá el Client Secret'); return; }
    setSaving(true);
    try {
      await axios.post('/api/google-calendar/credentials', { clientId: clientId.trim(), clientSecret: clientSecret.trim(), redirectUri });
      toast.success('Credenciales guardadas');
      onSaved();
    } catch (e: any) { toast.error(e.response?.data?.error || 'Error al guardar'); }
    finally { setSaving(false); }
  };

  const copyUri = () => { navigator.clipboard.writeText(redirectUri).then(() => toast.success('URI copiada')).catch(() => {}); };

  return (
    <Card><CardContent className="py-8 max-w-2xl mx-auto space-y-6">
      <div className="text-center space-y-2">
        <CalendarDaysIcon className="h-10 w-10 text-muted-foreground mx-auto" />
        <h3 className="font-semibold text-lg">Configurar Google Calendar</h3>
        <p className="text-sm text-muted-foreground">Pegá las credenciales que generaste en Google Cloud Console</p>
      </div>

      {/* Recordatorio del redirect URI */}
      <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-2">
        <p className="text-xs font-medium uppercase text-muted-foreground tracking-wide">Importante — en Google Cloud</p>
        <p className="text-sm">En tu OAuth client, dentro de <b>"URIs de redireccionamiento autorizados"</b>, tiene que estar exactamente esta URL:</p>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <code className="flex-1 text-xs bg-background border border-border rounded px-2 py-1.5 break-all">{redirectUri}</code>
          <Button variant="outline" size="sm" className="shrink-0 w-full sm:w-auto" onClick={copyUri}>Copiar</Button>
        </div>
      </div>

      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label>Client ID</Label>
          <Input value={clientId} onChange={e => setClientId(e.target.value)} placeholder="12735...apps.googleusercontent.com" />
        </div>
        <div className="space-y-1.5">
          <Label>Client Secret</Label>
          <Input type="password" value={clientSecret} onChange={e => setClientSecret(e.target.value)} placeholder="GOCSPX-..." />
          <p className="text-xs text-muted-foreground">Se guarda seguro en el servidor, nunca se muestra de vuelta.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          {canCancel && (
            <Button variant="outline" onClick={onCancel} disabled={saving} className="flex-1">Cancelar</Button>
          )}
          <Button onClick={save} disabled={saving} className="flex-1 bg-black dark:bg-white text-white dark:text-black">
            {saving ? 'Guardando...' : 'Guardar credenciales'}
          </Button>
        </div>
      </div>
    </CardContent></Card>
  );
};

// ── Panel de gestión de calendarios ───────────────────────────────────────
interface GCal { id: string; summary: string; primary: boolean; color?: string | null; accessRole?: string; visible: boolean; editable: boolean; }

const CalendarsManager: React.FC<{ onClose: () => void; onChanged: () => void }> = ({ onClose, onChanged }) => {
  const [cals, setCals] = useState<GCal[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [subId, setSubId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get('/api/google-calendar/calendars'); setCals(r.data.calendars || []); }
    catch { toast.error('No se pudieron cargar los calendarios'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const after = (data: any) => { if (data?.calendars) setCals(data.calendars); onChanged(); };

  const toggleVisible = async (c: GCal) => {
    setBusy(c.id);
    try { const r = await axios.post('/api/google-calendar/calendars/visibility', { calendarId: c.id, visible: !c.visible }); after(r.data); }
    catch (e: any) { toast.error(e.response?.data?.error || 'Error'); }
    finally { setBusy(null); }
  };
  const create = async () => {
    if (!newName.trim()) { toast.error('Poné un nombre'); return; }
    setBusy('create');
    try { const r = await axios.post('/api/google-calendar/calendars', { summary: newName.trim() }); setNewName(''); toast.success('Calendario creado'); after(r.data); }
    catch (e: any) { toast.error(e.response?.data?.error || 'Error al crear'); }
    finally { setBusy(null); }
  };
  const subscribe = async () => {
    if (!subId.trim()) { toast.error('Pegá el ID del calendario'); return; }
    setBusy('sub');
    try { const r = await axios.post('/api/google-calendar/calendars/subscribe', { calendarId: subId.trim() }); setSubId(''); toast.success('Suscripto'); after(r.data); }
    catch (e: any) { toast.error(e.response?.data?.error || 'No se pudo suscribir'); }
    finally { setBusy(null); }
  };
  const unsubscribe = async (c: GCal) => {
    if (!(await confirmDialog({ title: 'Quitar calendario', description: `¿Quitar "${c.summary}" de la cuenta? Se puede volver a suscribir después.`, confirmText: 'Quitar' }))) return;
    setBusy(c.id);
    try { const r = await axios.post('/api/google-calendar/calendars/unsubscribe', { calendarId: c.id }); after(r.data); }
    catch (e: any) { toast.error(e.response?.data?.error || 'Error'); }
    finally { setBusy(null); }
  };

  return (
    <Dialog open onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="w-[95vw] max-w-lg">
        <DialogHeader><DialogTitle>Gestionar calendarios</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <p className="text-xs text-muted-foreground">Tildá los que querés ver en ROCAM. El cambio también se refleja en tu Google Calendar.</p>

          {loading ? (
            <div className="py-8 text-center text-sm text-muted-foreground">Cargando...</div>
          ) : (
            <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
              {cals.map(c => (
                <div key={c.id} className="flex items-center gap-3 rounded-lg border border-border px-3 py-2">
                  <Tip label={c.primary ? 'El calendario principal siempre se muestra' : 'Mostrar/ocultar en ROCAM'}><input type="checkbox" checked={c.visible} disabled={busy === c.id || c.primary} onChange={() => toggleVisible(c)} className="shrink-0" /></Tip>
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: c.color || '#3b82f6' }} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{c.summary}{c.primary && <span className="text-xs text-muted-foreground font-normal"> · principal</span>}</p>
                    <p className="text-[11px] text-muted-foreground">{c.editable ? 'Podés editar' : 'Solo lectura'}</p>
                  </div>
                  {!c.primary && (
                    <Tip label="Quitar de la cuenta"><button onClick={() => unsubscribe(c)} disabled={busy === c.id} className="text-muted-foreground hover:text-red-600 shrink-0">
                      <TrashIcon className="h-4 w-4" />
                    </button></Tip>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Crear calendario nuevo */}
          <div className="rounded-lg border border-border p-3 space-y-2">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Crear calendario nuevo</Label>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Ej: Carreras, Campañas..." onKeyDown={e => { if (e.key === 'Enter') create(); }} className="w-full" />
              <Button onClick={create} disabled={busy === 'create'} className="shrink-0 w-full sm:w-auto">Crear</Button>
            </div>
          </div>

          {/* Suscribir por ID */}
          <div className="rounded-lg border border-border p-3 space-y-2">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Suscribir por ID</Label>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input value={subId} onChange={e => setSubId(e.target.value)} placeholder="calendario@group.calendar.google.com" onKeyDown={e => { if (e.key === 'Enter') subscribe(); }} className="w-full" />
              <Button variant="outline" onClick={subscribe} disabled={busy === 'sub'} className="shrink-0 w-full sm:w-auto">Suscribir</Button>
            </div>
            <p className="text-[11px] text-muted-foreground">Pegá el ID de un calendario público. Google no permite buscar calendarios por nombre desde acá.</p>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cerrar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ── Modal de evento ────────────────────────────────────────────────────────
const EventModal: React.FC<{
  event: GEvent | null; defaultDay: number | null; month: number; year: number;
  onClose: () => void; onSaved: () => void; onToggleHide: (e: GEvent) => void;
}> = ({ event, defaultDay, month, year, onClose, onSaved, onToggleHide }) => {
  const pad = (n: number) => String(n).padStart(2, '0');
  const dayStr = (d: number) => `${year}-${pad(month)}-${pad(d)}`;
  const initDate = event ? (event.start.slice(0, 10)) : dayStr(defaultDay || 1);
  const initStartTime = event && !event.allDay ? new Date(event.start).toTimeString().slice(0, 5) : '';
  const initEndTime = event && !event.allDay && event.end ? new Date(event.end).toTimeString().slice(0, 5) : '';

  const [form, setForm] = useState({
    title: event?.title || '', description: event?.description || '', location: event?.location || '',
    date: initDate, allDay: event ? !!event.allDay : true,
    startTime: initStartTime || '09:00', endTime: initEndTime || '10:00',
  });
  const [saving, setSaving] = useState(false);

  const readOnly = !!event?.readOnly;

  const save = async () => {
    if (!form.title.trim()) { toast.error('Poné un título'); return; }
    setSaving(true);
    try {
      let start: string, end: string;
      if (form.allDay) { start = form.date; end = form.date; }
      else { start = new Date(`${form.date}T${form.startTime}:00`).toISOString(); end = new Date(`${form.date}T${form.endTime}:00`).toISOString(); }
      const payload = { title: form.title.trim(), description: form.description, location: form.location, start, end, allDay: form.allDay, calendarId: event?.calendarId };
      if (event) await axios.put(`/api/google-calendar/events/${event.id}`, payload);
      else await axios.post('/api/google-calendar/events', payload);
      toast.success(event ? 'Evento actualizado' : 'Evento creado');
      onSaved();
    } catch (e: any) { toast.error(e.response?.data?.error || 'Error al guardar'); }
    finally { setSaving(false); }
  };
  const remove = async () => {
    if (!event) return;
    if (!(await confirmDialog({ title: 'Eliminar evento', description: `¿Eliminar "${event.title}"?`, confirmText: 'Eliminar' }))) return;
    try { await axios.delete(`/api/google-calendar/events/${event.id}`, { params: { calendarId: event.calendarId } }); toast.success('Eliminado'); onSaved(); }
    catch { toast.error('Error al eliminar'); }
  };

  return (
    <Dialog open onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="w-[95vw] max-w-md">
        <DialogHeader><DialogTitle>{!event ? 'Nuevo evento' : readOnly ? 'Evento' : 'Editar evento'}</DialogTitle></DialogHeader>
        {readOnly ? (
          <div className="space-y-3">
            {event?.calendarName && (
              <Badge variant="secondary" className="gap-1.5">
                <span className="w-2 h-2 rounded-full" style={{ background: event?.color || '#888' }} /> {event.calendarName}
              </Badge>
            )}
            <h3 className="font-semibold text-lg">{event?.title}</h3>
            <p className="text-sm text-muted-foreground">
              {event?.allDay ? 'Todo el día' : `${fmtTime(event!.start)}${event?.end ? ' – ' + fmtTime(event.end) : ''}`}
              {' · '}{eventLocalDate(event!).toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
            {event?.location && <p className="text-sm"><span className="text-muted-foreground">Ubicación:</span> {event.location}</p>}
            {event?.description && <p className="text-sm whitespace-pre-wrap">{event.description}</p>}
            <p className="text-xs text-muted-foreground italic">Este evento es de solo lectura (calendario "{event?.calendarName || 'externo'}"). No se puede editar desde acá.</p>
            {event?.htmlLink && <a href={event.htmlLink} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 dark:text-blue-400 flex items-center gap-1"><ArrowTopRightOnSquareIcon className="h-3 w-3" /> Abrir en Google Calendar</a>}
            <DialogFooter className="flex-row justify-between sm:justify-between">
              {event?.hidden
                ? <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => onToggleHide(event!)}>Volver a mostrar</Button>
                : <Tip label="No lo borra de Google, solo lo esconde en ROCAM"><Button variant="ghost" size="sm" className="text-muted-foreground gap-1.5" onClick={() => onToggleHide(event!)}>Ocultar en ROCAM</Button></Tip>}
              <Button variant="outline" onClick={onClose}>Cerrar</Button>
            </DialogFooter>
          </div>
        ) : (
        <>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label>Título *</Label><Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Ej: Campaña 20% alineación" autoFocus /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Fecha</Label><DatePicker value={form.date} onChange={v => setForm(f => ({ ...f, date: v }))} /></div>
            <div className="space-y-1.5 flex flex-col">
              <Label>Todo el día</Label>
              <label className="flex items-center gap-2 h-9 text-sm cursor-pointer">
                <input type="checkbox" checked={form.allDay} onChange={e => setForm(f => ({ ...f, allDay: e.target.checked }))} />
                <span className="text-muted-foreground">{form.allDay ? 'Sí' : 'No'}</span>
              </label>
            </div>
          </div>
          {!form.allDay && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Desde</Label><Input type="time" value={form.startTime} onChange={e => setForm(f => ({ ...f, startTime: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>Hasta</Label><Input type="time" value={form.endTime} onChange={e => setForm(f => ({ ...f, endTime: e.target.value }))} /></div>
            </div>
          )}
          <div className="space-y-1.5"><Label>Descripción</Label><textarea rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-none" placeholder="Detalle (opcional)" /></div>
          <div className="space-y-1.5"><Label>Ubicación</Label><Input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} placeholder="Opcional" /></div>
          {event?.htmlLink && <a href={event.htmlLink} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 dark:text-blue-400 flex items-center gap-1"><ArrowTopRightOnSquareIcon className="h-3 w-3" /> Abrir en Google Calendar</a>}
        </div>
        <DialogFooter className="flex-row justify-between sm:justify-between">
          {event ? <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-500/10 gap-1.5" onClick={remove}><TrashIcon className="h-4 w-4" /> Eliminar</Button> : <span />}
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancelar</Button>
            <Button onClick={save} disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</Button>
          </div>
        </DialogFooter>
        </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GoogleCalendarView;
